import type { CrossChainStatusResponse, CrossChainTransactionStatus } from './types';

export const FAILED_CROSS_CHAIN_STATUSES = new Set<CrossChainTransactionStatus>([
  'origin_tx_reverted',
  'bridge_failed',
  'unknown',
]);

export const BRIDGE_CROSS_CHAIN_STATUSES = new Set<CrossChainTransactionStatus>([
  'bridge_pending',
  'bridge_filled',
  'bridge_failed',
]);

export const TERMINAL_CROSS_CHAIN_STATUSES = new Set<CrossChainTransactionStatus>([
  'bridge_filled',
  ...FAILED_CROSS_CHAIN_STATUSES,
]);

export const isFailedCrossChainStatus = (
  status: CrossChainTransactionStatus | undefined,
): boolean => !!status && FAILED_CROSS_CHAIN_STATUSES.has(status);

export const isBridgeCrossChainStatus = (
  status: CrossChainTransactionStatus | undefined,
): boolean => !!status && BRIDGE_CROSS_CHAIN_STATUSES.has(status);

export const isTerminalCrossChainStatus = (
  status: CrossChainTransactionStatus | undefined,
): boolean => !!status && TERMINAL_CROSS_CHAIN_STATUSES.has(status);

/**
 * A `bridge_failed` whose refund is still processing. The bridge auto-refunds
 * the user after a failure, so the trade has not truly settled yet - we keep
 * polling so the refund outcome (and the History row) can progress from
 * "Refund Pending" to "Refunded" instead of freezing on the failure snapshot.
 */
export const isRefundInProgress = (tradeStatus: CrossChainStatusResponse | undefined): boolean =>
  tradeStatus?.status === 'bridge_failed' && tradeStatus.failure?.status === 'refund_pending';

/**
 * Whether a cross-chain trade has reached a final outcome, accounting for the
 * post-failure refund lifecycle. Unlike {@link isTerminalCrossChainStatus} (which
 * only looks at the top-level status), a `bridge_failed` with a still-pending
 * refund is NOT settled.
 */
export const isCrossChainOutcomeSettled = (
  tradeStatus: CrossChainStatusResponse | undefined,
): boolean => isTerminalCrossChainStatus(tradeStatus?.status) && !isRefundInProgress(tradeStatus);

export const getCrossChainStatusPollingInterval = (serviceTime: number | undefined): number => {
  if (serviceTime !== undefined && serviceTime <= 5) return 1_000;
  if (serviceTime !== undefined && serviceTime <= 30) return 3_000;
  return 5_000;
};

export const formatCrossChainDuration = (
  seconds: number | undefined,
  approximate = false,
): string => {
  if (seconds === undefined) return '...';
  const prefix = approximate ? '~' : '';
  if (seconds < 60) return `${prefix}${seconds} sec`;
  const minutes = Math.ceil(seconds / 60);
  return `${prefix}${minutes} ${minutes === 1 ? 'min' : 'mins'}`;
};
